import { useCallback, useEffect, useState } from 'react'
import {
  safeGetLocalStorageItem,
  safeSetLocalStorageItem,
} from '@mc/browser-storage'
import { useTranslation } from '@mc/i18n'
import { logger } from '@mc/next-logger'
import { useStaticData } from '@mc/static-data-react'
import { createRateArticle, rateArticle } from 'libs/client/api/articles'
import {
  getRecipeSubsection,
  parseISO8601Duration,
  formatIso8601Duration,
} from 'libs/utils'
import EmailRecipeButton from './EmailRecipeButton'
import Metric from './Metric'
import Ratings from './Ratings'
import { trackIngredientClick } from './events'

const formatDuration = (duration) =>
  duration ? formatIso8601Duration(parseISO8601Duration(duration)) : ''

const Recipe = () => {
  const { article } = useStaticData()
  const { t } = useTranslation('@mc/next')
  const [userRating, setUserRating] = useState(null)

  const recipe = getRecipeSubsection(article.articleSubsections)

  const onRate = useCallback(
    (rating, currentRating) => {
      safeSetLocalStorageItem(`rating-${article.id}`, JSON.stringify(rating))

      if (userRating) {
        rateArticle(article, rating, currentRating)
      } else {
        setUserRating(rating)
        createRateArticle(article, rating)
      }
    },
    [article, userRating],
  )

  useEffect(() => {
    const ratingJSON = safeGetLocalStorageItem(`rating-${article.id}`)

    if (!ratingJSON) return

    try {
      const rating = JSON.parse(ratingJSON)

      if (rating) setUserRating(rating)
    } catch (err) {
      logger.error(
        `Failed to parse localStorage item rating-${article.id}`,
        err,
      )
    }

    if (recipe) trackIngredientClick(recipe)
  }, [recipe, article.id])

  if (!recipe) return <></>

  const prepTime = formatDuration(recipe.prepTime)
  const totalTime = formatDuration(recipe.totalTime)
  const cookTime = formatDuration(recipe.cookTime)

  return (
    <section id={recipe.id} className='mc-my-10 mc-bg-gray-700 mc-p-10'>
      <h2 className='mc-text-h2 mc-mb-8'>{recipe.title}</h2>
      <div className='row mc-mb-4'>
        <EmailRecipeButton />
        <Ratings userRating={userRating} onRate={onRate} />
      </div>
      <div className='row mc-mb-4'>
        {recipe.recipeYield && (
          <Metric
            name={t('article.metrics.makes')}
            value={recipe.recipeYield}
          />
        )}
        {prepTime && (
          <Metric name={t('article.metrics.prepTime')} value={prepTime} />
        )}
        {totalTime && (
          <Metric name={t('article.metrics.totalTime')} value={totalTime} />
        )}
        {cookTime && (
          <Metric name={t('article.metrics.cookTime')} value={cookTime} />
        )}
      </div>
      <div className='mc-text-large highlight-shareable'>
        <h3 className='mc-text--bold mc-text-h3 mc-mb-4'>
          {t('article.subsections.recipe.title')}
        </h3>
        <div
          className='mc-mb-4'
          dangerouslySetInnerHTML={{ __html: recipe.recipeIngredient }}
        />
        <div
          className='mc-mb-4'
          dangerouslySetInnerHTML={{ __html: recipe.recipeInstructions }}
        />
      </div>
    </section>
  )
}

export default Recipe
